/**
 * MRL Cart Bridge
 * Shared cart API for theme sections.
 *
 * Use from any section:
 *   await window.MRLCart.addItem(variantId, 1, { openDrawer: true });
 *   await window.MRLCart.addItems([{ id: variantId, quantity: 1 }], { openDrawer: true });
 *   await window.MRLCart.changeItem(lineItemKey, quantity, { refreshDrawer: true });
 */
(function () {
  if (window.MRLCart) return;

  function root() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  function getDrawer() {
    return document.querySelector('.cart-drawer');
  }

  function getDrawerPanel() {
    return document.querySelector('#cart-drawer');
  }

  function getPageWrapper() {
    return document.querySelector('.layout-wrapper');
  }

  function getDrawerSection() {
    return document.querySelector('#shopify-section-cart-drawer');
  }

  function announce(message) {
    var el = document.getElementById('cart-live-region');
    if (el) el.textContent = message;
  }

  function announceError(message) {
    var el = document.getElementById('cart-alert-region');
    if (el) el.textContent = message;
  }

  function cartSummary(cart) {
    if (!cart) return '';
    var count = cart.item_count;
    return count + (count === 1 ? ' item' : ' items') + ' in cart, subtotal ' + formatMoney(cart.total_price) + '.';
  }

  var lastFocusedBeforeOpen = null;

  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }

    return `$${(Number(cents || 0) / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  }

  function openDrawer() {
    const drawer = getDrawer();
    if (!drawer) return false;
    if (drawer.classList.contains('cart-drawer--active')) return true;

    lastFocusedBeforeOpen = document.activeElement;

    drawer.classList.add('cart-drawer--active');
    drawer.removeAttribute('inert');
    drawer.removeAttribute('aria-hidden');
    document.body.style.overflowY = 'hidden';

    const pageWrapper = getPageWrapper();
    if (pageWrapper) {
      pageWrapper.setAttribute('inert', '');
      pageWrapper.setAttribute('aria-hidden', 'true');
    }

    const panel = getDrawerPanel();
    const focusTarget = panel && panel.querySelector('.cart-drawer-header-right-close, button, a[href]');
    if (focusTarget) focusTarget.focus();

    return true;
  }

  function closeDrawer() {
    const drawer = getDrawer();
    if (!drawer) return false;

    drawer.classList.remove('cart-drawer--active');
    drawer.setAttribute('inert', '');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflowY = '';

    const pageWrapper = getPageWrapper();
    if (pageWrapper) {
      pageWrapper.removeAttribute('inert');
      pageWrapper.removeAttribute('aria-hidden');
    }

    if (lastFocusedBeforeOpen && typeof lastFocusedBeforeOpen.focus === 'function') {
      lastFocusedBeforeOpen.focus();
    }
    lastFocusedBeforeOpen = null;

    return true;
  }

  async function refreshDrawer() {
    const currentSection = getDrawerSection();
    if (!currentSection) return false;

    const response = await fetch(root() + '?section_id=cart-drawer', {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      cache: 'no-store'
    });

    if (!response.ok) return false;

    const html = await response.text();
    const nextSection = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector('#shopify-section-cart-drawer');

    if (!nextSection) return false;

    currentSection.innerHTML = nextSection.innerHTML;
    document.dispatchEvent(new CustomEvent('mrl:cart:drawer-refreshed', { bubbles: true }));

    // A qty/remove button is disabled (and therefore blurred) before its request
    // even starts, so focus is reliably gone by the time we get here - restore it
    // to the close button rather than leaving it stranded on <body>.
    const drawer = getDrawer();
    if (drawer && drawer.classList.contains('cart-drawer--active') && !drawer.contains(document.activeElement)) {
      const fallbackFocus = drawer.querySelector('.cart-drawer-header-right-close, #Continue');
      if (fallbackFocus) fallbackFocus.focus();
    }

    return true;
  }

  async function getCart() {
    const response = await fetch(root() + 'cart.js', {
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      cache: 'no-store'
    });

    if (!response.ok) throw new Error('Cart fetch failed');
    return response.json();
  }

  function updateVisibleCartTotals(cart) {
    if (!cart) return;

    document.querySelectorAll('.total_amount').forEach((el) => {
      el.textContent = formatMoney(cart.total_price);
    });

    document.querySelectorAll('.bag_value').forEach((el) => {
      el.textContent = `(${cart.item_count} items)`;
    });
  }

  async function addItem(variantId, quantity, options) {
    const opts = Object.assign({ refreshDrawer: true, openDrawer: false }, options || {});

    const response = await fetch(root() + 'cart/add.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        id: Number(variantId),
        quantity: Number(quantity || 1)
      })
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status) {
      const message = data.description || data.message || 'Cart add failed';
      announceError('Sorry, we couldn\'t add that to your cart. ' + message);
      throw new Error(message);
    }

    const [cart] = await Promise.all([
      getCart().catch(e => { console.warn('[MRL Cart] Cart fetch after add failed:', e); return null; }),
      opts.refreshDrawer ? refreshDrawer() : Promise.resolve()
    ]);
    updateVisibleCartTotals(cart);
    if (opts.openDrawer) openDrawer();
    announce(((data && data.title) ? data.title + ' added to cart. ' : 'Added to cart. ') + cartSummary(cart));

    document.dispatchEvent(new CustomEvent('mrl:cart:updated', {
      bubbles: true,
      detail: { action: 'addItem', item: data, cart: cart }
    }));

    return data;
  }

  async function addItems(items, options) {
    const opts = Object.assign({ refreshDrawer: true, openDrawer: false }, options || {});

    const normalizedItems = (items || [])
      .filter((item) => item && item.id)
      .map((item) => ({
        id: Number(item.id),
        quantity: Number(item.quantity || 1)
      }));

    if (!normalizedItems.length) {
      throw new Error('No cart items provided');
    }

    const response = await fetch(root() + 'cart/add.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ items: normalizedItems })
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status) {
      const message = data.description || data.message || 'Cart add failed';
      announceError('Sorry, we couldn\'t add those items to your cart. ' + message);
      throw new Error(message);
    }

    const [cart] = await Promise.all([
      getCart().catch(e => { console.warn('[MRL Cart] Cart fetch after add-items failed:', e); return null; }),
      opts.refreshDrawer ? refreshDrawer() : Promise.resolve()
    ]);
    updateVisibleCartTotals(cart);
    if (opts.openDrawer) openDrawer();
    announce('Added to cart. ' + cartSummary(cart));

    document.dispatchEvent(new CustomEvent('mrl:cart:updated', {
      bubbles: true,
      detail: { action: 'addItems', items: data, cart: cart }
    }));

    return data;
  }

  async function changeItem(lineItemKey, quantity, options) {
    const opts = Object.assign({ refreshDrawer: false, openDrawer: false }, options || {});

    const response = await fetch(root() + 'cart/change.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        id: lineItemKey,
        quantity: Number(quantity)
      })
    });

    const cart = await response.json().catch(() => ({}));
    if (!response.ok || cart.status) {
      const message = cart.description || cart.message || 'Cart change failed';
      announceError('Sorry, we couldn\'t update your cart. ' + message);
      throw new Error(message);
    }

    updateVisibleCartTotals(cart);
    if (opts.refreshDrawer) await refreshDrawer();
    if (opts.openDrawer) openDrawer();
    announce('Cart updated. ' + cartSummary(cart));

    document.dispatchEvent(new CustomEvent('mrl:cart:updated', {
      bubbles: true,
      detail: { action: 'changeItem', cart: cart }
    }));

    return cart;
  }

  window.MRLCart = {
    root,
    getCart,
    addItem,
    addItems,
    changeItem,
    refreshDrawer,
    openDrawer,
    closeDrawer,
    updateVisibleCartTotals,
    formatMoney,
    announce,
    announceError
  };

  // Backward-compatible alias for code already migrated during cleanup.
  window.MRLCartDrawer = window.MRLCartDrawer || {
    open: openDrawer,
    close: closeDrawer,
    refresh: refreshDrawer,
    getCart: getCart,
    changeItem: changeItem,
    add: function (variantId, quantity) {
      return addItem(variantId, quantity || 1, {
        refreshDrawer: true,
        openDrawer: true
      });
    },
    addItems: function (items) {
      return addItems(items, {
        refreshDrawer: true,
        openDrawer: true
      });
    }
  };
})();
